// One Section 18 policy as a form control: its options (with any change cost and locks), the
// "affects next season" preview line from the policy's `affects` hook, validation messages and
// the Ledger's record of past changes.
import type { JusticeResponse, Policies, PolicyId, PreviewLine, SchoolSubject } from '../../sim/api';
import type { PoliciesVM, PolicyControlVM, PolicyOptionVM } from '../../viewModels';
import { jobLabel } from '../../viewModels';
import { humanize, seasonName, template, whole } from '../format';
import { policyPreviewText, uiText } from '../text/en';

interface Props {
  vm: PolicyControlVM;
  lists: Pick<PoliciesVM, 'subjects' | 'justiceResponses' | 'housingModes'>;
  canEdit: boolean;
  onChange: (id: PolicyId, value: unknown) => void;
}

function optionLabel(id: PolicyId, value: unknown): string {
  if (typeof value === 'boolean') return value ? uiText.policies.on : uiText.policies.off;
  const key = String(value);
  return uiText.policyOption[id]?.[key] ?? humanize(key);
}

function subjectLabel(s: SchoolSubject): string {
  return s === 'literacy' ? 'Literacy' : jobLabel(s);
}

export function previewText(line: PreviewLine): string {
  const text = policyPreviewText[line.key];
  if (text !== undefined) return template(text, line.params);
  const params = Object.entries(line.params)
    .map(([k, v]) => `${k} ${String(v)}`)
    .join(', ');
  return `${humanize(line.key)}${params === '' ? '' : ` (${params})`}.`;
}

function costText(o: PolicyOptionVM): string {
  if (o.locked !== null) return ` (${uiText.policies.locked})`;
  return o.changeCost < 0 ? ` (trust ${whole(o.changeCost)})` : '';
}

function Choice({ vm, canEdit, onChange }: Omit<Props, 'lists'>) {
  const selected = JSON.stringify(vm.drafted);
  return (
    <select
      id={`policy-${vm.id}`}
      value={selected}
      disabled={!canEdit}
      onChange={(e) => {
        const o = vm.options.find((x) => x.key === e.currentTarget.value);
        if (o !== undefined) onChange(vm.id, o.value);
      }}
    >
      {vm.options.map((o) => (
        <option key={o.key} value={o.key} disabled={o.locked !== null && o.key !== selected}>
          {optionLabel(vm.id, o.value)}
          {costText(o)}
        </option>
      ))}
    </select>
  );
}

function Toggle({ vm, canEdit, onChange }: Omit<Props, 'lists'>) {
  const on = vm.drafted === true;
  const off = vm.options.find((o) => o.value === false);
  return (
    <label class="field--check">
      <input
        id={`policy-${vm.id}`}
        type="checkbox"
        checked={on}
        disabled={!canEdit}
        onChange={(e) => onChange(vm.id, e.currentTarget.checked)}
      />
      <span>
        {on ? uiText.policies.on : uiText.policies.off}
        {on && off !== undefined && off.changeCost < 0 && (
          <span class="hint"> (Off: trust {whole(off.changeCost)})</span>
        )}
      </span>
    </label>
  );
}

function Subjects({ vm, lists, canEdit, onChange }: Props) {
  const current = vm.drafted as [SchoolSubject, SchoolSubject];
  const set = (i: 0 | 1, value: SchoolSubject): void => {
    const next: [SchoolSubject, SchoolSubject] = [current[0], current[1]];
    next[i] = value;
    if (next[0] !== next[1]) onChange(vm.id, next);
  };
  return (
    <span class="policy__pair">
      {([0, 1] as const).map((i) => (
        <select
          key={i}
          id={i === 0 ? `policy-${vm.id}` : undefined}
          aria-label={`${vm.label} ${i + 1}`}
          value={current[i]}
          disabled={!canEdit}
          onChange={(e) => set(i, e.currentTarget.value as SchoolSubject)}
        >
          {lists.subjects.map((s) => (
            <option key={s} value={s} disabled={s === current[i === 0 ? 1 : 0]}>
              {subjectLabel(s)}
            </option>
          ))}
        </select>
      ))}
    </span>
  );
}

function Housing({ vm, canEdit, onChange }: Props) {
  const value = vm.drafted as Policies['housing'];
  return (
    <span class="policy__pair">
      <select
        id={`policy-${vm.id}`}
        aria-label={`${vm.label}: ${uiText.policies.mode}`}
        value={value.mode}
        disabled={!canEdit}
        onChange={(e) =>
          onChange(vm.id, { ...value, mode: e.currentTarget.value as Policies['housing']['mode'] })
        }
      >
        {vm.options.map((o) => {
          const mode = (o.value as Policies['housing']).mode;
          return (
            <option key={o.key} value={mode}>
              {optionLabel(vm.id, mode)}
              {costText(o)}
            </option>
          );
        })}
      </select>
      <label class="field--check">
        <input
          type="checkbox"
          checked={value.allowOvercrowding}
          disabled={!canEdit}
          onChange={(e) => onChange(vm.id, { ...value, allowOvercrowding: e.currentTarget.checked })}
        />
        <span>{uiText.policies.overcrowding}</span>
      </label>
    </span>
  );
}

function Justice({ vm, canEdit, onChange }: Props) {
  const value = vm.drafted as Policies['justice'];
  return (
    <span class="policy__pair">
      <select
        id={`policy-${vm.id}`}
        aria-label={`${vm.label}: ${uiText.policies.response}`}
        value={value.response}
        disabled={!canEdit}
        onChange={(e) =>
          onChange(vm.id, { ...value, response: e.currentTarget.value as JusticeResponse })
        }
      >
        {vm.options.map((o) => {
          const response = (o.value as Policies['justice']).response;
          return (
            <option key={o.key} value={response} disabled={o.locked !== null && response !== value.response}>
              {optionLabel(vm.id, response)}
              {costText(o)}
            </option>
          );
        })}
      </select>
      <label class="field--check">
        <input
          type="checkbox"
          checked={value.askEachCase}
          disabled={!canEdit}
          onChange={(e) => onChange(vm.id, { ...value, askEachCase: e.currentTarget.checked })}
        />
        <span>{uiText.policies.askEachCase}</span>
      </label>
    </span>
  );
}

function Priority({ vm }: Props) {
  const groups = vm.drafted as string[];
  return (
    <span>
      <ol class="policy__order">
        {groups.map((g) => (
          <li key={g}>{humanize(g)}</li>
        ))}
      </ol>
      <span class="hint">{uiText.policies.priorityNote}</span>
    </span>
  );
}

export function PolicyControl(props: Props) {
  const { vm } = props;
  const t = uiText.policies;
  let control;
  switch (vm.kind) {
    case 'choice':
      control = <Choice {...props} />;
      break;
    case 'toggle':
      control = <Toggle {...props} />;
      break;
    case 'subjects':
      control = <Subjects {...props} />;
      break;
    case 'housing':
      control = <Housing {...props} />;
      break;
    case 'justice':
      control = <Justice {...props} />;
      break;
    case 'priority':
      control = <Priority {...props} />;
      break;
  }
  return (
    <div class={`policy${vm.changed ? ' is-changed' : ''}`}>
      <div class="policy__head">
        <label class="policy__label" for={vm.kind === 'priority' ? undefined : `policy-${vm.id}`}>
          {vm.label}
        </label>
        {vm.changed && <span class="tag">{t.pending}</span>}
      </div>
      <div class="policy__control">{control}</div>
      <p class="policy__note hint">{uiText.policyNote[vm.id] ?? ''}</p>
      {vm.preview.map((line, i) => (
        <p key={i} class="policy__preview">
          <span aria-hidden="true">→ </span>
          {previewText(line)}
        </p>
      ))}
      {vm.changeCost < 0 && <p class="policy__cost">{t.trustCost(whole(vm.changeCost))}</p>}
      {vm.problem !== null && (
        <p class="policy__problem" role="alert">
          <span aria-hidden="true">✗ </span>
          {vm.problem}
        </p>
      )}
      {vm.history.length > 0 && (
        <details class="policy__history">
          <summary>
            {t.history} ({vm.history.length})
          </summary>
          <ul>
            {vm.history.map((h, i) => (
              <li key={i}>{t.changedOn(seasonName(h.season), h.year, humanize(h.value))}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
